import { useState, useEffect, useCallback } from 'react';
import { CreditCard, Banknote, Smartphone, Gift, Receipt, Mail, MessageSquare, Printer, X, Check, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

import { API } from '@/config/api';
import { getAuthToken } from '@/lib/authToken';

// Screen states
const SCREENS = {
  TIP: 'tip',
  PAYMENT: 'payment',
  PROCESSING: 'processing',
  RECEIPT: 'receipt',
  THANK_YOU: 'thank_you',
  IDLE: 'idle'
};

export default function CustomerFacingDisplay() {
  const [screen, setScreen] = useState(SCREENS.IDLE);
  const [orderData, setOrderData] = useState(null);
  const [tipAmount, setTipAmount] = useState(0);
  const [tipPercentage, setTipPercentage] = useState(null);
  const [customTip, setCustomTip] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [showPhoneInput, setShowPhoneInput] = useState(false);
  const [showEmailInput, setShowEmailInput] = useState(false);

  // Listen for order data from POS (via localStorage or WebSocket)
  useEffect(() => {
    const checkForOrder = () => {
      const pendingOrder = localStorage.getItem('cfd_pending_order');
      if (pendingOrder) {
        const data = JSON.parse(pendingOrder);
        setOrderData(data);
        setScreen(SCREENS.TIP);
        localStorage.removeItem('cfd_pending_order');
      }
    };

    // Check every second for new orders
    const interval = setInterval(checkForOrder, 1000);
    
    // Also listen for storage events from POS
    window.addEventListener('storage', checkForOrder);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', checkForOrder);
    };
  }, []);

  // Auto-dismiss thank you screen after 15 seconds
  useEffect(() => {
    if (screen === SCREENS.THANK_YOU) {
      const timer = setTimeout(() => {
        resetToIdle();
      }, 15000);
      return () => clearTimeout(timer);
    }
  }, [screen]);

  const resetToIdle = () => {
    setScreen(SCREENS.IDLE);
    setOrderData(null);
    setTipAmount(0);
    setTipPercentage(null);
    setCustomTip('');
    setSelectedPayment(null);
    setCustomerPhone('');
    setCustomerEmail('');
    setShowPhoneInput(false);
    setShowEmailInput(false);
  };

  const calculateTip = (percentage) => {
    if (!orderData) return 0;
    return (orderData.subtotal * percentage) / 100;
  };

  const handleTipSelect = (percentage) => {
    setTipPercentage(percentage);
    setTipAmount(calculateTip(percentage));
    setCustomTip('');
  };

  const handleCustomTip = (value) => {
    const amount = parseFloat(value) || 0;
    setCustomTip(value);
    setTipAmount(amount);
    setTipPercentage(null);
  };

  const handleNoTip = () => {
    setTipPercentage(0);
    setTipAmount(0);
    setCustomTip('');
  };

  const proceedToPayment = () => {
    // Send tip back to POS
    localStorage.setItem('cfd_tip_selected', JSON.stringify({
      orderId: orderData?.orderId,
      tipAmount,
      tipPercentage
    }));
    setScreen(SCREENS.PAYMENT);
  };

  const handlePaymentSelect = async (method) => {
    setSelectedPayment(method);
    setScreen(SCREENS.PROCESSING);
    
    // Notify POS of payment method selection
    localStorage.setItem('cfd_payment_selected', JSON.stringify({
      orderId: orderData?.orderId,
      paymentMethod: method,
      tipAmount
    }));

    // For card payments, wait for terminal response
    // For cash/other, POS handles it
    if (method === 'credit_card') {
      // Wait for payment completion from POS
      const checkPayment = setInterval(() => {
        const result = localStorage.getItem('cfd_payment_result');
        if (result) {
          const data = JSON.parse(result);
          localStorage.removeItem('cfd_payment_result');
          clearInterval(checkPayment);
          
          if (data.success) {
            setScreen(SCREENS.RECEIPT);
          } else {
            toast.error(data.message || 'Payment failed');
            setScreen(SCREENS.PAYMENT);
          }
        }
      }, 500);

      // Timeout after 2 minutes
      setTimeout(() => clearInterval(checkPayment), 120000);
    } else {
      // Non-card payments - wait for POS confirmation
      setTimeout(() => setScreen(SCREENS.RECEIPT), 1000);
    }
  };

  const handleReceiptOption = async (option) => {
    // Send receipt preference to POS
    localStorage.setItem('cfd_receipt_option', JSON.stringify({
      orderId: orderData?.orderId,
      option,
      phone: customerPhone,
      email: customerEmail
    }));

    // Handle receipt delivery: the ONE receipt door (payments/receipts.py) with the register's
    // own sign-in; the answer says whether it was really sent.
    const channel = option === 'sms' && customerPhone ? 'text' : option === 'email' && customerEmail ? 'email' : null;
    if (channel && orderData?.orderId) {
      try {
        const res = await fetch(`${API}/checkout/receipts/order/${orderData.orderId}/${channel}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAuthToken() || ''}`,
            'Idempotency-Key': `cfd-${channel}-${orderData.orderId}-${Date.now()}`,
          },
          body: JSON.stringify(channel === 'text' ? { phone: customerPhone } : { email: customerEmail }),
        });
        const body = await res.json().catch(() => null);
        if (res.ok && body?.sent) toast.success(channel === 'text' ? 'Receipt texted' : 'Receipt e-mailed');
        else toast.error((body && (body.message || (typeof body.detail === 'string' ? body.detail : body.detail?.message))) || 'The receipt was not sent - ask the cashier');
      } catch (e) {
        toast.error('The receipt was not sent - ask the cashier');
      }
    }

    setScreen(SCREENS.THANK_YOU);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // ==================== IDLE SCREEN ====================
  if (screen === SCREENS.IDLE) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-32 h-32 mx-auto mb-8 rounded-full bg-blue-600/20 flex items-center justify-center">
            <DollarSign className="w-16 h-16 text-blue-400" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Chain29</h1>
          <p className="text-xl text-slate-400">Customer Display Ready</p>
        </div>
      </div>
    );
  }

  // ==================== TIP SELECTION SCREEN ====================
  if (screen === SCREENS.TIP) {
    const tipOptions = [15, 18, 20, 25];
    const total = (orderData?.subtotal || 0) + (orderData?.tax || 0) + tipAmount;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8 flex flex-col">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Add a Tip?</h1>
          <p className="text-xl text-slate-400">Your generosity is appreciated!</p>
        </div>

        {/* Order Summary */}
        <Card className="bg-slate-800/50 border-slate-700 mb-8">
          <CardContent className="p-6">
            <div className="flex justify-between text-xl text-slate-300 mb-2">
              <span>Subtotal</span>
              <span>{formatCurrency(orderData?.subtotal)}</span>
            </div>
            <div className="flex justify-between text-xl text-slate-300 mb-2">
              <span>Tax</span>
              <span>{formatCurrency(orderData?.tax)}</span>
            </div>
            {tipAmount > 0 && (
              <div className="flex justify-between text-xl text-blue-400 mb-2">
                <span>Tip</span>
                <span>{formatCurrency(tipAmount)}</span>
              </div>
            )}
            <div className="border-t border-slate-600 pt-4 mt-4">
              <div className="flex justify-between text-3xl font-bold text-white">
                <span>Total</span>
                <span>{formatCurrency(total)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tip Options */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {tipOptions.map((pct) => (
            <Button
              key={pct}
              onClick={() => handleTipSelect(pct)}
              className={`h-24 text-2xl font-bold rounded-2xl transition-all ${
                tipPercentage === pct
                  ? 'bg-blue-600 hover:bg-blue-700 text-white ring-4 ring-blue-400'
                  : 'bg-slate-700 hover:bg-slate-600 text-white'
              }`}
            >
              <div className="text-center">
                <div>{pct}%</div>
                <div className="text-sm opacity-75">{formatCurrency(calculateTip(pct))}</div>
              </div>
            </Button>
          ))}
        </div>

        {/* Custom Tip */}
        <div className="flex gap-4 mb-6">
          <Input
            type="number"
            placeholder="Custom Amount"
            value={customTip}
            onChange={(e) => handleCustomTip(e.target.value)}
            className="h-16 text-2xl bg-slate-700 border-slate-600 text-white text-center"
          />
          <Button
            onClick={handleNoTip}
            className={`h-16 px-8 text-xl rounded-xl ${
              tipPercentage === 0
                ? 'bg-slate-600 ring-4 ring-slate-400'
                : 'bg-slate-700 hover:bg-slate-600'
            } text-white`}
          >
            No Tip
          </Button>
        </div>

        {/* Continue Button */}
        <Button
          onClick={proceedToPayment}
          className="h-20 text-2xl font-bold bg-green-600 hover:bg-green-700 text-white rounded-2xl mt-auto"
        >
          Continue to Payment
        </Button>
      </div>
    );
  }

  // ==================== PAYMENT METHOD SCREEN ====================
  if (screen === SCREENS.PAYMENT) {
    const paymentMethods = [
      { id: 'credit_card', label: 'Credit/Debit Card', icon: CreditCard, color: 'blue' },
      { id: 'cash', label: 'Cash', icon: Banknote, color: 'green' },
      { id: 'zelle', label: 'Zelle', icon: Smartphone, color: 'purple' },
      { id: 'venmo', label: 'Venmo', icon: Smartphone, color: 'cyan' },
    ];

    const total = (orderData?.subtotal || 0) + (orderData?.tax || 0) + tipAmount;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8 flex flex-col">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Select Payment Method</h1>
          <p className="text-3xl text-blue-400 font-bold">Total: {formatCurrency(total)}</p>
        </div>

        {/* Payment Options */}
        <div className="grid grid-cols-2 gap-6 flex-1">
          {paymentMethods.map((method) => (
            <Button
              key={method.id}
              onClick={() => handlePaymentSelect(method.id)}
              className={`h-40 text-2xl font-bold rounded-3xl flex flex-col items-center justify-center gap-4
                bg-slate-700 hover:bg-slate-600 text-white border-2 border-slate-600 hover:border-${method.color}-500
                transition-all hover:scale-105`}
            >
              <method.icon className="w-16 h-16" />
              <span>{method.label}</span>
            </Button>
          ))}
        </div>

        {/* Back Button */}
        <Button
          onClick={() => setScreen(SCREENS.TIP)}
          variant="outline"
          className="h-16 text-xl mt-6 border-slate-600 text-slate-300 hover:bg-slate-700"
        >
          Back to Tip Selection
        </Button>
      </div>
    );
  }

  // ==================== PROCESSING SCREEN ====================
  if (screen === SCREENS.PROCESSING) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-32 h-32 mx-auto mb-8 rounded-full bg-blue-600/30 flex items-center justify-center animate-pulse">
            <CreditCard className="w-16 h-16 text-blue-400" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Processing Payment</h1>
          <p className="text-2xl text-slate-400">
            {selectedPayment === 'credit_card' 
              ? 'Please insert, tap, or swipe your card'
              : 'Waiting for confirmation...'}
          </p>
          <div className="mt-8 flex justify-center gap-2">
            <div className="w-4 h-4 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-4 h-4 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-4 h-4 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>
      </div>
    );
  }

  // ==================== RECEIPT OPTIONS SCREEN ====================
  if (screen === SCREENS.RECEIPT) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8 flex flex-col">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-green-600/30 flex items-center justify-center">
            <Check className="w-12 h-12 text-green-400" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">Payment Successful!</h1>
          <p className="text-xl text-slate-400">How would you like your receipt?</p>
        </div>

        {/* Receipt Options */}
        <div className="grid grid-cols-2 gap-6 flex-1 max-w-3xl mx-auto w-full">
          {/* Print Receipt */}
          <Button
            onClick={() => handleReceiptOption('print')}
            className="h-40 text-2xl font-bold rounded-3xl flex flex-col items-center justify-center gap-4
              bg-slate-700 hover:bg-slate-600 text-white transition-all hover:scale-105"
          >
            <Printer className="w-16 h-16" />
            <span>Print Receipt</span>
          </Button>

          {/* SMS Receipt */}
          <Button
            onClick={() => setShowPhoneInput(true)}
            className="h-40 text-2xl font-bold rounded-3xl flex flex-col items-center justify-center gap-4
              bg-slate-700 hover:bg-slate-600 text-white transition-all hover:scale-105"
          >
            <MessageSquare className="w-16 h-16" />
            <span>Text Receipt</span>
          </Button>

          {/* Email Receipt */}
          <Button
            onClick={() => setShowEmailInput(true)}
            className="h-40 text-2xl font-bold rounded-3xl flex flex-col items-center justify-center gap-4
              bg-slate-700 hover:bg-slate-600 text-white transition-all hover:scale-105"
          >
            <Mail className="w-16 h-16" />
            <span>Email Receipt</span>
          </Button>

          {/* No Receipt */}
          <Button
            onClick={() => handleReceiptOption('none')}
            className="h-40 text-2xl font-bold rounded-3xl flex flex-col items-center justify-center gap-4
              bg-slate-700 hover:bg-slate-600 text-white transition-all hover:scale-105"
          >
            <X className="w-16 h-16" />
            <span>No Receipt</span>
          </Button>
        </div>

        {/* Phone Input Modal */}
        {showPhoneInput && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-8 z-50">
            <Card className="bg-slate-800 border-slate-700 w-full max-w-lg">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-white mb-4 text-center">Enter Phone Number</h2>
                <Input
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  className="h-16 text-2xl bg-slate-700 border-slate-600 text-white text-center mb-6"
                  autoFocus
                />
                <div className="flex gap-4">
                  <Button
                    onClick={() => setShowPhoneInput(false)}
                    variant="outline"
                    className="flex-1 h-14 text-xl border-slate-600 text-slate-300"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      setShowPhoneInput(false);
                      handleReceiptOption('sms');
                    }}
                    className="flex-1 h-14 text-xl bg-green-600 hover:bg-green-700 text-white"
                    disabled={!customerPhone}
                  >
                    Send
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Email Input Modal */}
        {showEmailInput && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-8 z-50">
            <Card className="bg-slate-800 border-slate-700 w-full max-w-lg">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-white mb-4 text-center">Enter Email Address</h2>
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  className="h-16 text-2xl bg-slate-700 border-slate-600 text-white text-center mb-6"
                  autoFocus
                />
                <div className="flex gap-4">
                  <Button
                    onClick={() => setShowEmailInput(false)}
                    variant="outline"
                    className="flex-1 h-14 text-xl border-slate-600 text-slate-300"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      setShowEmailInput(false);
                      handleReceiptOption('email');
                    }}
                    className="flex-1 h-14 text-xl bg-green-600 hover:bg-green-700 text-white"
                    disabled={!customerEmail}
                  >
                    Send
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    );
  }

  // ==================== THANK YOU SCREEN ====================
  if (screen === SCREENS.THANK_YOU) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-40 h-40 mx-auto mb-8 rounded-full bg-green-600/30 flex items-center justify-center">
            <Check className="w-20 h-20 text-green-400" />
          </div>
          <h1 className="text-6xl font-bold text-white mb-6">Thank You!</h1>
          <p className="text-2xl text-slate-400 mb-8">We appreciate your business</p>
          <p className="text-lg text-slate-500">This screen will close automatically</p>
          <div className="mt-8 w-64 mx-auto h-2 bg-slate-700 rounded-full overflow-hidden">
            <div className="h-full bg-green-500 animate-shrink-bar"></div>
          </div>
        </div>
        
        <style>{`
          @keyframes shrink-bar {
            from { width: 100%; }
            to { width: 0%; }
          }
          .animate-shrink-bar {
            animation: shrink-bar 15s linear forwards;
          }
        `}</style>
      </div>
    );
  }

  return null;
}
